import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, CheckCircle2, FileText, HardHat, Loader2, Megaphone, MessageCircle, PenLine, Star } from "lucide-react";
import { OwnerDailyDigest } from "../components/OwnerDailyDigest";
import { OwnerWeeklyList } from "../components/OwnerWeeklyList";
import { OwnerNotifications } from "../components/OwnerNotifications";
import { OwnerSiteSurveyCard } from "../components/OwnerSiteSurveyCard";
import { fmtD, fmtDT } from "../lib/date";
import { makeT, esOf, pickPair, readOwnerLang, storeOwnerLang, normOwnerLang, OWNER_LANGS, OWNER_LANG_LABEL } from "../lib/ownerI18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PHASE_EN = {
  "开工前准备": "Pre-Construction", "拆除": "Demolition", "基础": "Foundation", "框架结构": "Framing",
  "粗装 MEP": "Rough MEP", "精装前": "Pre-Finishes", "精装收尾": "Finishes", "市政收尾验收": "City Final",
};

function SigPad({ onChange, clearLabel }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const pos = (e) => {
    const c = ref.current, r = c.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return [(p.clientX - r.left) * (c.width / r.width), (p.clientY - r.top) * (c.height / r.height)];
  };
  const start = (e) => {
    drawing.current = true;
    const ctx = ref.current.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a";
    const [x, y] = pos(e);
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const [x, y] = pos(e);
    ctx.lineTo(x, y); ctx.stroke();
    dirty.current = true;
  };
  const end = () => {
    if (drawing.current && dirty.current) onChange(ref.current.toDataURL("image/png"));
    drawing.current = false;
  };
  const clear = () => {
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    onChange(null);
  };
  return (
    <div>
      <canvas ref={ref} width={500} height={160} data-testid="owner-signoff-pad"
        className="w-full border-2 border-dashed border-slate-300 rounded-xl bg-slate-50 touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <button type="button" onClick={clear} className="text-[10px] text-slate-400 hover:text-slate-600 mt-1" data-testid="owner-signoff-clear">{clearLabel}</button>
    </div>
  );
}

export default function OwnerPortal() {
  const { token } = useParams();
  const [lang, setLang] = useState(() => readOwnerLang());
  const zh = lang === "zh";
  const T = makeT(lang);
  const pn = (name) => (zh ? name : lang === "es" ? (/[\u4e00-\u9fff]/.test(name || "") ? esOf(PHASE_EN[name] || name) : name) : (PHASE_EN[name] || name));
  const setLangTo = (n) => {
    if (n === lang) return;
    setLang(n); storeOwnerLang(n);
    axios.post(`${BACKEND}/api/public/owner-hub/${token}/lang`, { lang: n }).catch(() => {});
    axios.get(`${BACKEND}/api/public/owner-hub/${token}`, { params: { lang: n } }).then((r) => setD(r.data)).catch(() => {});
  };
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [big, setBig] = useState(null);
  const [signFor, setSignFor] = useState(null);
  const [signName, setSignName] = useState("");
  const [signData, setSignData] = useState(null);
  const [signBusy, setSignBusy] = useState(false);
  const [signMsg, setSignMsg] = useState("");
  const [asking, setAsking] = useState(false);
  const [q, setQ] = useState("");
  const [ackBusy, setAckBusy] = useState(false);
  const [confirmFor, setConfirmFor] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [confirmSig, setConfirmSig] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");
  const [preChecked, setPreChecked] = useState([]);
  const [preName, setPreName] = useState("");
  const [preSig, setPreSig] = useState(null);
  const [preBusy, setPreBusy] = useState(false);
  const [preMsg, setPreMsg] = useState("");
  const [satScore, setSatScore] = useState(0);
  const [satComment, setSatComment] = useState("");
  const [satBusy, setSatBusy] = useState(false);
  const [satMsg, setSatMsg] = useState("");
  const satRef = useRef(null);
  const load = useCallback(() => {
    const stored = localStorage.getItem("ali_owner_lang");
    axios.get(`${BACKEND}/api/public/owner-hub/${token}`, { params: stored ? { lang: stored } : {} })
      .then((r) => {
        setD(r.data);
        if (!stored && r.data?.ownerLang) { const l = normOwnerLang(r.data.ownerLang); setLang(l); storeOwnerLang(l); }
      })
      .catch((e) => setErr(e?.detailText || "链接无效或已失效 Invalid or expired link"));
  }, [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.satisfaction?.eligible && window.location.hash === "#rate" && satRef.current) {
      setTimeout(() => satRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 400);
    }
  }, [d]);
  useEffect(() => {
    if (d?.satisfaction?.score) { setSatScore(d.satisfaction.score); setSatComment(d.satisfaction.comment || ""); }
  }, [d]);
  const rate = async (rid, score) => {
    try { await axios.post(`${BACKEND}/api/public/owner-hub/${token}/reports/${rid}/rate`, { score }); load(); }
    catch { /* noop */ }
  };
  const submitSatisfaction = async () => {
    setSatMsg("");
    if (!satScore) { setSatMsg(T("请先点星评分", "Please pick a star rating first")); return; }
    setSatBusy(true);
    try {
      await axios.post(`${BACKEND}/api/public/owner-hub/${token}/satisfaction`, { score: satScore, comment: satComment });
      setSatMsg(""); load();
    } catch (e) { setSatMsg(e?.detailText || T("提交失败，请重试", "Submit failed, please retry")); }
    setSatBusy(false);
  };
  const ackBrief = async (action) => {
    setAckBusy(true);
    try {
      await axios.post(`${BACKEND}/api/public/owner-hub/${token}/brief/${d.brief.id}/ack`, { action, text: q });
      setAsking(false); setQ("");
      load();
    } catch { /* noop */ }
    setAckBusy(false);
  };
  const submitSign = async (sid) => {
    setSignMsg("");
    if (!signName.trim()) { setSignMsg(T("请填写签字人姓名", "Full name required")); return; }
    if (!signData) { setSignMsg(T("请先手写签名", "Signature required")); return; }
    setSignBusy(true);
    try {
      await axios.post(`${BACKEND}/api/public/owner-hub/${token}/signoffs/${sid}/sign`, { name: signName, signature: signData });
      setSignFor(null); setSignData(null);
      load();
    } catch (e) { setSignMsg(e?.detailText || T("提交失败，请重试", "Submit failed, please retry")); }
    setSignBusy(false);
  };
  const submitConfirm = async (cid) => {
    setConfirmMsg("");
    if (!confirmName.trim()) { setConfirmMsg(T("请填写签字人姓名", "Full name required")); return; }
    if (!confirmSig) { setConfirmMsg(T("请先手写签名", "Signature required")); return; }
    setConfirmBusy(true);
    try {
      await axios.post(`${BACKEND}/api/public/owner-hub/${token}/confirmations/${cid}/sign`, { name: confirmName, signature: confirmSig });
      setConfirmFor(null); setConfirmSig(null);
      load();
    } catch (e) { setConfirmMsg(e?.detailText || T("提交失败，请重试", "Submit failed, please retry")); }
    setConfirmBusy(false);
  };
  const signPrecon = async () => {
    setPreMsg("");
    if (preChecked.length !== 3) { setPreMsg(T("请先逐份勾选确认全部三份文件", "Please confirm all 3 documents")); return; }
    if (!preName.trim()) { setPreMsg(T("请填写签字人姓名", "Full name required")); return; }
    if (!preSig) { setPreMsg(T("请先手写签名", "Signature required")); return; }
    setPreBusy(true);
    try {
      await axios.post(`${BACKEND}/api/public/owner-hub/${token}/precon/sign`, { name: preName, signature: preSig, checked: preChecked });
      load();
    } catch (e) { setPreMsg(e?.detailText || T("提交失败，请重试", "Submit failed, please retry")); }
    setPreBusy(false);
  };
  if (err) return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6"><p className="text-slate-400 text-sm" data-testid="owner-error">{err}</p></div>;
  if (!d) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
  const pi = d.phaseIndex;
  const purl = (p) => `${BACKEND}/api/public/owner-hub/${token}/photo/${p.id}`;
  const fmtT = (iso) => fmtDT(iso);
  const pendingSign = (d.signoffs || []).filter((s) => s.status === "pending");
  const sat = d.satisfaction || {};
  return (
    <div className="min-h-screen bg-slate-100 pb-10" data-testid="owner-portal-page">
      <div className="bg-slate-900 text-white px-5 pt-6 pb-9">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[11px] tracking-widest text-slate-400 font-bold flex items-center gap-1.5"><HardHat className="h-3.5 w-3.5" />{T("ALIGCUS 工程进度 · OWNER PORTAL", "ALIGCUS OWNER PORTAL · PROJECT PROGRESS")}</p>
            <div className="shrink-0 inline-flex rounded-full border border-slate-600 overflow-hidden" data-testid="owner-lang-toggle" data-lang={lang} role="group" aria-label="Language">
              {OWNER_LANGS.map((l) => <button key={l} type="button" onClick={() => setLangTo(l)} data-testid={`owner-lang-${l}`} aria-pressed={lang === l}
                className={`text-[11px] font-bold px-2.5 py-1 transition-colors ${lang === l ? "bg-white text-slate-900" : "text-slate-200 hover:bg-slate-800"}`}>{OWNER_LANG_LABEL[l]}</button>)}
            </div>
          </div>
          <h1 className="text-xl font-black mt-1.5" data-testid="owner-address" data-keep-title>{d.address}</h1>
          <p className="text-xs text-slate-300 mt-1">{T("业主", "Owner")} {d.clientName || "-"} · {T("项目经理", "PM")} {d.manager || "-"} · {T("开工", "Started")} {fmtD(d.startDate) || "-"}{d.type ? ` · ${d.type}` : ""}</p>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 -mt-5 space-y-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4" data-testid="owner-progress">
          <div className="flex items-end justify-between mb-2">
            <p className="text-sm font-bold text-slate-800">{T("工程进度", "Progress")}</p>
            <p className="text-2xl font-black text-emerald-600" data-testid="owner-progress-pct">{d.progress}%</p>
          </div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden mb-3"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${d.progress}%` }} /></div>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-1">
            {d.phases.map((n, x) => {
              const done = pi > x || pi >= 8, cur = pi === x;
              const g = (d.gates || [])[x];
              return (
                <div key={x} className="text-center">
                  <div className={`mx-auto h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${done ? "bg-emerald-500 text-white" : cur ? "bg-amber-400 text-white animate-pulse" : "bg-slate-100 text-slate-400"}`}>
                    {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : x + 1}
                  </div>
                  <p className={`text-[9px] mt-1 leading-tight ${cur ? "text-amber-600 font-bold" : "text-slate-400"}`}>{pn(n)}</p>
                  {g && <p className="text-[8px] text-slate-300">{g.done}/{g.total}</p>}
                </div>
              );
            })}
          </div>
          {d.timing && <p className="text-[11px] text-slate-400 mt-2">{T(`当前阶段「${d.timing.phaseName}」已进行 ${d.timing.stayed} 天`, `Current phase "${pn(d.timing.phaseName)}" — day ${d.timing.stayed}`)}</p>}
        </div>
        <OwnerWeeklyList token={token} lang={lang} T={T} />
        <OwnerDailyDigest token={token} lang={lang} T={T} />
        {(d.evidenceUpdates || []).length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-indigo-100 p-4" data-testid="owner-updates">
            <p className="text-sm font-bold text-slate-800 mb-1.5 flex items-center gap-1.5"><Megaphone className="h-4 w-4 text-indigo-500" />{T("工程更新提示", "Recent Updates")}</p>
            <div className="space-y-1">
              {d.evidenceUpdates.map((u) => (
                <p key={u.taskId} className="text-[11px] text-slate-600" data-testid={`owner-update-${u.taskId}`}>
                  🔔 {T(`关键节点「${u.title}」已上传完工凭证 ${u.count} 份`, `Milestone "${u.title}" — ${u.count} proof photo(s) uploaded`)}
                  {u.phaseName && <span className="ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{pn(u.phaseName)}</span>}
                  <span className="ml-1 text-slate-300">{String(u.at || "").slice(5, 16).replace("T", " ")}</span>
                </p>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">{T("凭证照片见下方「关键节点完工凭证」区", "See photos in the Milestone Evidence section below")}</p>
          </div>
        )}
        {d.deliveryCert && (
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-2xl shadow-sm p-4 text-white" data-testid="owner-delivery-cert">
            <p className="text-sm font-bold">🏡 {T("恭喜交房！Congratulations on your new home!", "Congratulations on your new home!")}</p>
            <p className="text-[11px] text-emerald-50 mt-1">{T("您的项目已正式交付。交付证书含质保说明与四项签收记录，请下载留存。", "Your project is officially delivered — download your certificate with warranty info & closeout receipts.")}</p>
            <a href={`${BACKEND}/api/public/owner-hub/${token}/delivery-cert`} data-testid="owner-delivery-cert-download"
              className="inline-block mt-2.5 text-xs font-bold rounded-xl px-4 py-2 bg-white text-emerald-700 hover:bg-emerald-50">⬇ {T("下载交付证书", "Download Certificate PDF")}</a>
          </div>
        )}
        {sat.eligible && (
          <div ref={satRef} id="rate" className="bg-white rounded-2xl shadow-sm border border-amber-200 p-4" data-testid="owner-satisfaction">
            <p className="text-sm font-bold text-slate-800 mb-1 flex items-center gap-1.5"><Star className="h-4 w-4 text-amber-500" />{T("入住满意度回访", "Move-in Satisfaction Survey")}</p>
            <p className="text-[11px] text-slate-400 mb-2">{T("感谢选择 ALIGCUS！请为我们的整体服务打分，您的反馈将直达管理层。", "Thank you for choosing ALIGCUS! Rate our overall service — your feedback goes straight to management.")}</p>
            <div className="flex items-center gap-1 mb-2" data-testid="owner-sat-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`owner-sat-star-${n}`} onClick={() => setSatScore(n)}
                  className={`text-2xl leading-none transition-transform hover:scale-110 ${n <= satScore ? "text-amber-400" : "text-slate-200"}`}>★</button>
              ))}
              {satScore > 0 && <span className="text-xs font-bold text-amber-600 ml-1">{satScore}/5</span>}
            </div>
            <textarea value={satComment} onChange={(e) => setSatComment(e.target.value)} rows={2}
              placeholder={T("入住体验、保修需求或任何建议…（可选）", "Move-in experience, warranty needs or suggestions… (optional)")}
              className="w-full text-xs rounded-xl border border-slate-200 px-3 py-2" data-testid="owner-sat-comment" />
            {satMsg && <p className="text-[11px] text-red-500 mt-1" data-testid="owner-sat-msg">{satMsg}</p>}
            <button data-testid="owner-sat-submit" disabled={satBusy} onClick={submitSatisfaction}
              className="w-full mt-2 text-xs font-bold rounded-xl py-2 bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50">
              {satBusy ? T("提交中…", "Submitting…") : sat.score ? T("更新我的评价", "Update my rating") : T("提交评价", "Submit rating")}</button>
            {sat.score && <p className="text-[11px] font-semibold text-emerald-600 mt-1.5" data-testid="owner-sat-done">✓ {T(`已收到您的 ${sat.score} 星评价，感谢反馈！`, `Your ${sat.score}-star rating is in — thank you!`)}{sat.submittedAt ? ` · ${fmtD(sat.submittedAt)}` : ""}</p>}
          </div>
        )}
        {d.precon && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4" data-testid="owner-precon">
            <p className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1.5"><FileText className="h-4 w-4 text-sky-600" />{T("开工前文件打包签署", "Pre-Construction Package")}
              {d.precon.status === "signed" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">✓ {T("已签署", "Signed")}</span>}
            </p>
            <div className="space-y-2">
              {d.precon.items.map((it) => (
                <div key={it.key} className="rounded-xl bg-slate-50 p-2.5" data-testid={`owner-precon-item-${it.key}`}>
                  <label className="flex items-start gap-2 cursor-pointer">
                    {d.precon.status !== "signed" && (
                      <input type="checkbox" checked={preChecked.includes(it.key)}
                        onChange={(e) => setPreChecked(e.target.checked ? [...preChecked, it.key] : preChecked.filter((k) => k !== it.key))}
                        className="mt-0.5 h-3.5 w-3.5 accent-sky-600" data-testid={`owner-precon-check-${it.key}`} />
                    )}
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-700">{d.precon.status === "signed" ? "✓ " : ""}{it.label}</p>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {it.files.map((f) => (
                          <a key={f.fileId} href={`${BACKEND}/api/public/owner-hub/${token}/precon-file/${f.fileId}`} target="_blank" rel="noreferrer"
                            className="text-[10px] font-semibold text-sky-600 underline" data-testid={`owner-precon-file-${f.fileId}`}>📄 {f.filename}</a>
                        ))}
                      </div>
                    </div>
                  </label>
                </div>
              ))}
            </div>
            {d.precon.status === "signed" ? (
              <p className="text-[11px] font-semibold text-emerald-600 mt-2" data-testid="owner-precon-signed">✓ {d.precon.signedName} {T("已打包签署", "signed the package")} · {fmtD(d.precon.signedAt)} · {T("签名凭证已归档", "Signature archived")}</p>
            ) : (
              <div className="mt-2.5 space-y-2">
                <p className="text-[10px] text-slate-400">{T("请逐份查看并勾选确认以上三份文件（场地图/变更单/开工文件），然后签名一次即完成打包签署。", "Review & check all three documents (site plan / change order / pre-con docs), then sign once below.")}</p>
                <input value={preName} onChange={(e) => setPreName(e.target.value)} placeholder={T("签字人姓名", "Full name")}
                  className="w-full text-sm rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="owner-precon-name" />
                <SigPad onChange={setPreSig} clearLabel={T("清除重签", "Clear & redo")} />
                {preMsg && <p className="text-[11px] text-red-500" data-testid="owner-precon-msg">{preMsg}</p>}
                <button data-testid="owner-precon-sign" disabled={preBusy} onClick={signPrecon}
                  className="w-full text-xs font-bold rounded-xl py-2 bg-slate-900 text-white disabled:opacity-50">{preBusy ? T("提交中…", "Submitting…") : T("确认全部三份并打包签署", "Sign Package")}</button>
              </div>
            )}
          </div>
        )}
        <OwnerSiteSurveyCard token={token} s={d.siteSurvey} lang={lang} />
        {d.brief && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4" data-testid="owner-brief-card">
            <p className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1.5"><Megaphone className="h-4 w-4 text-indigo-600" />{T("本周工程简报", "Weekly Briefing")}
              <span className="text-[10px] font-normal text-slate-400">{fmtD(d.brief.createdAt)}</span></p>
            <pre className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed bg-slate-50 rounded-xl p-3">{d.brief.body}</pre>
            {d.brief.ackAction ? (
              <p className="mt-2 text-[11px] font-semibold text-emerald-600" data-testid="owner-brief-acked">
                {d.brief.ackAction === "read" ? T("✓ 您已确认阅读本周简报，感谢！", "✓ You've confirmed this week's briefing — thank you!") : T("✓ 您的提问已提交，项目经理会尽快回复您。", "✓ Your question was submitted — your PM will reply soon.")}</p>
            ) : (
              <div className="mt-2.5 space-y-2">
                <div className="flex gap-2">
                  <button data-testid="owner-brief-ack-read" disabled={ackBusy} onClick={() => ackBrief("read")}
                    className="flex-1 text-xs font-bold rounded-xl py-2 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">✓ {T("已阅，一切了解", "Got it, all clear")}</button>
                  <button data-testid="owner-brief-ask-toggle" onClick={() => setAsking(!asking)}
                    className={`flex-1 text-xs font-bold rounded-xl py-2 border ${asking ? "bg-indigo-600 text-white border-indigo-600" : "text-indigo-600 border-indigo-300 hover:bg-indigo-50"}`}>{T("我有疑问", "I have a question")}</button>
                </div>
                {asking && (
                  <div className="flex gap-2">
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={T("请输入您的问题，项目经理会尽快回复…", "Type your question — your PM will reply soon…")}
                      className="flex-1 text-xs rounded-xl border border-slate-200 px-3 py-2" data-testid="owner-brief-question-input" />
                    <button data-testid="owner-brief-question-send" disabled={!q.trim() || ackBusy} onClick={() => ackBrief("question")}
                      className="text-xs font-bold rounded-xl px-4 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">{T("提交", "Send")}</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {(d.reports || []).length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4" data-testid="owner-weekly-reports">
            <p className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1.5"><FileText className="h-4 w-4 text-sky-600" />{T("每周进度小结", "Weekly Updates")}</p>
            <div className="space-y-3">
              {d.reports.map((r, i) => (
                <div key={r.id} className={`rounded-xl p-3 ${i === 0 ? "bg-sky-50" : "bg-slate-50"}`} data-testid={`owner-report-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] font-bold text-slate-600">{r.week} {T("周报", "Weekly")} · {fmtD(r.generatedAt)}{r.phaseName ? ` ·「${pn(r.phaseName)}」` : ""}</p>
                    <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{T("进度", "Progress")} {r.progress}%</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1.5 leading-relaxed" data-testid={`owner-report-text-${r.id}`}>{r.aiText}</p>
                  {(r.doneTitles || []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {r.doneTitles.map((t, j) => <span key={j} className="text-[9px] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-500">✓ {t}</span>)}
                    </div>
                  )}
                  {(r.upcoming || []).length > 0 && <p className="text-[10px] text-slate-400 mt-1.5">{T("下一步：", "Next: ")}{r.upcoming.join("；")}</p>}
                  {(r.photoIds || []).length > 0 && (
                    <div className="mt-2 grid grid-cols-6 gap-1">
                      {r.photoIds.map((fid) => (
                        <button key={fid} onClick={() => setBig({ id: fid })} className="relative rounded-lg overflow-hidden bg-slate-100 aspect-square">
                          <img src={purl({ id: fid })} alt="weekly" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-1 flex-wrap" data-testid={`owner-report-rate-${r.id}`}>
                    <span className="text-[10px] text-slate-400 mr-1">{r.rating ? T("您的评价：", "Your rating: ") : T("本周服务满意吗？点一下表情告诉我们", "Happy with this week? Tap an emoji")}</span>
                    {["😞", "😕", "😐", "🙂", "😍"].map((em, n) => (
                      <button key={n} data-testid={`owner-report-rate-${r.id}-${n + 1}`} onClick={() => rate(r.id, n + 1)}
                        className={`text-lg leading-none transition-transform hover:scale-125 ${r.rating === n + 1 ? "scale-125" : r.rating ? "opacity-30 grayscale" : ""}`}>{em}</button>
                    ))}
                    {r.rating ? <span className="text-[10px] text-emerald-600 font-semibold ml-1">{T("感谢您的评价！", "Thanks for the feedback!")}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {(d.confirmations || []).length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4" data-testid="owner-confirms">
            <p className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1.5"><FileText className="h-4 w-4 text-indigo-600" />{T("阶段确认书", "Stage Confirmations")}</p>
            <div className="space-y-2.5">
              {d.confirmations.map((c) => (
                <div key={c.id} className={`rounded-xl p-3 ${c.status === "signed" ? "bg-emerald-50" : "bg-amber-50"}`} data-testid={`owner-confirm-${c.id}`}>
                  <p className="text-xs font-bold text-slate-800">{pickPair(c.titleZh, c.titleEn, lang)}{zh && c.titleEn && <span className="ml-1.5 text-[10px] font-normal text-slate-400">{c.titleEn}</span>}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{pickPair(c.descZh, c.descEn, lang)}</p>
                  <ul className="mt-1.5 space-y-1.5">
                    {(c.items || []).map((it, i) => (
                      <li key={i} className="text-[10px] text-slate-600 leading-relaxed">☑ {pickPair(it.zh, it.en, lang)}{zh && it.en && <><br /><span className="text-slate-400 pl-3.5 block">{it.en}</span></>}</li>
                    ))}
                  </ul>
                  {c.status === "signed" ? (
                    <p className="text-[11px] font-semibold text-emerald-600 mt-2 flex flex-wrap items-center gap-x-2" data-testid={`owner-confirm-signed-${c.id}`}>✓ {c.signedName} {T("已签署", "signed")} · {fmtD(c.signedAt)} · {T("签名凭证已归档", "Signature archived")}
                      <a href={`${BACKEND}/api/public/owner-hub/${token}/confirmations/${c.id}.pdf?lang=${lang}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-indigo-600 hover:underline" data-testid={`owner-confirm-pdf-${c.id}`}><FileText className="h-3 w-3" />{T("下载已签署 PDF", "Download signed PDF")}</a>
                    </p>
                  ) : confirmFor === c.id ? (
                    <div className="space-y-2 mt-2">
                      <input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={T("签字人姓名", "Full name")}
                        className="w-full text-sm rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="owner-confirm-name" />
                      <SigPad onChange={setConfirmSig} clearLabel={T("清除重签", "Clear & redo")} />
                      {confirmMsg && <p className="text-[11px] text-red-500" data-testid="owner-confirm-msg">{confirmMsg}</p>}
                      <div className="flex gap-2">
                        <button data-testid={`owner-confirm-submit-${c.id}`} disabled={confirmBusy} onClick={() => submitConfirm(c.id)}
                          className="flex-1 text-xs font-bold rounded-xl py-2 bg-indigo-600 text-white disabled:opacity-50">{confirmBusy ? T("提交中…", "Submitting…") : T("确认并签署", "Confirm & Sign")}</button>
                        <button onClick={() => setConfirmFor(null)} className="text-xs rounded-xl px-3 border border-slate-200 text-slate-500">{T("取消", "Cancel")}</button>
                      </div>
                    </div>
                  ) : (
                    <button data-testid={`owner-confirm-open-${c.id}`} onClick={() => { setConfirmFor(c.id); setConfirmSig(null); setConfirmMsg(""); }}
                      className="w-full mt-2 text-xs font-bold rounded-xl py-2 bg-slate-900 text-white">{T("签署确认书", "Review & Sign")}</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {(d.signoffs || []).length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4" data-testid="owner-signoffs">
            <p className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1.5"><PenLine className="h-4 w-4 text-emerald-600" />{T("阶段签字确认", "Phase Sign-offs")}
              {pendingSign.length > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{pendingSign.length} {T("项待您确认", "awaiting you")}</span>}
            </p>
            <div className="space-y-2">
              {d.signoffs.map((s) => (
                <div key={s.id} className={`rounded-xl p-3 ${s.status === "signed" ? "bg-emerald-50" : "bg-amber-50"}`} data-testid={`owner-signoff-${s.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-bold text-slate-700">{T(`「${s.phaseName}」阶段已完成`, `"${pn(s.phaseName)}" phase completed`)}</p>
                    {s.status === "signed" ? (
                      <span className="text-[10px] font-bold text-emerald-600">✓ {s.signedName} {T("已签字", "signed")} · {fmtT(s.signedAt)}</span>
                    ) : (
                      <button data-testid={`owner-signoff-open-${s.id}`} onClick={() => { setSignFor(signFor === s.id ? null : s.id); setSignData(null); setSignMsg(""); }}
                        className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-700">{T("签字确认", "Sign")}</button>
                    )}
                  </div>
                  {signFor === s.id && s.status !== "signed" && (
                    <div className="mt-2 space-y-2">
                      <input value={signName} onChange={(e) => setSignName(e.target.value)} placeholder={T("签字人姓名", "Full name")}
                        className="w-full text-sm rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid="owner-signoff-name" />
                      <SigPad onChange={setSignData} clearLabel={T("清除重签", "Clear & redo")} />
                      {signMsg && <p className="text-[11px] text-red-500" data-testid="owner-signoff-msg">{signMsg}</p>}
                      <button data-testid="owner-signoff-submit" disabled={signBusy} onClick={() => submitSign(s.id)}
                        className="w-full text-sm font-bold rounded-xl py-2 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                        {signBusy ? T("提交中…", "Submitting…") : T("确认并提交签字", "Confirm & submit signature")}
                      </button>
                      <p className="text-[10px] text-slate-400">{T("签字确认代表您已知悉该阶段完成情况，签名将归档至项目文件。", "Signing confirms you acknowledge this phase's completion; your signature is archived with the project.")}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {(d.milestones || []).length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4" data-testid="owner-milestones">
            <p className="text-sm font-bold text-slate-800 mb-1 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" />{T("关键节点完工凭证", "Milestone Completion Evidence")} <span className="text-[11px] font-normal text-slate-400">{d.milestones.length} {T("项", "")}</span></p>
            <p className="text-[10px] text-slate-400 mb-2">{T("以下为需留证的关键工序完成时上传的现场凭证照片", "Photos uploaded as proof when key tasks were completed")}</p>
            <div className="space-y-3">
              {d.milestones.map((m) => (
                <div key={m.taskId} data-testid={`owner-milestone-${m.taskId}`}>
                  <p className="text-[11px] font-semibold text-slate-700">
                    {m.phaseName && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 mr-1.5">{pn(m.phaseName)}</span>}
                    {m.title}
                    <span className="ml-1.5 text-slate-300 font-normal">{m.doneAt ? fmtD(m.doneAt) : ""}</span>
                  </p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 mt-1">
                    {m.photos.map((p) => (
                      <button key={p.id} onClick={() => setBig(p)} className="relative rounded-lg overflow-hidden bg-slate-100 aspect-[4/3]" data-testid={`owner-milestone-photo-${p.id}`}>
                        <img src={purl(p)} alt={p.filename || "evidence"} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4" data-testid="owner-photos">
          <p className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1.5"><Camera className="h-4 w-4 text-indigo-500" />{T("现场照片", "Site Photos")} <span className="text-[11px] font-normal text-slate-400">{(d.photos || []).length} {T("张", "")}</span></p>
          {(d.photos || []).length === 0 ? <p className="text-xs text-slate-300 text-center py-8">{T("暂无现场照片", "No photos yet")}</p> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {d.photos.map((p) => (
                <button key={p.id} onClick={() => setBig(p)} className="relative rounded-xl overflow-hidden bg-slate-100 aspect-[4/3]" data-testid={`owner-photo-${p.id}`}>
                  <img src={purl(p)} alt={p.filename || "site"} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  {p.phaseName && <span className="absolute top-1.5 left-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-black/55 text-white">{pn(p.phaseName)}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4" data-testid="owner-discussions">
          <p className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1.5"><MessageCircle className="h-4 w-4 text-violet-500" />{T("项目沟通", "Project Messages")} <span className="text-[10px] font-normal text-slate-400">{T("（业主可见）", "(shared with you)")}</span></p>
          {(d.discussions || []).length === 0 ? <p className="text-xs text-slate-300 text-center py-6">{T("暂无沟通记录", "No messages yet")}</p> : (
            <div className="space-y-2.5">
              {d.discussions.map((x) => (
                <div key={x.id} className="rounded-xl bg-slate-50 p-3" data-testid={`owner-disc-${x.id}`}>
                  <p className="text-[11px]">
                    <span className="font-bold text-slate-700">{x.authorName}</span>
                    {x.dept && <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-white text-slate-500 border border-slate-200">{x.dept}</span>}
                    <span className="ml-1.5 text-slate-300">{fmtT(x.at)}</span>
                  </p>
                  <p className="text-xs text-slate-600 mt-1">{x.text}</p>
                  {(x.replies || []).map((r, i) => (
                    <p key={i} className="text-[11px] text-slate-500 mt-1 pl-2 border-l-2 border-slate-200"><b>{r.authorName}</b>: {r.text}</p>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        <OwnerNotifications url={`${BACKEND}/api/public/owner-hub/${token}/notifications`} lang={lang} />
        <p className="text-center text-[10px] text-slate-400">{T("本页面为只读工程进度页 · 如有疑问请联系您的项目经理", "Read-only progress page · questions? Contact your project manager")}{d.manager ? `（${d.manager}）` : ""}</p>
      </div>
      {big && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setBig(null)}>
          <img src={purl(big)} alt="site" className="max-h-[90vh] max-w-full rounded-xl object-contain" />
        </div>
      )}
    </div>
  );
}
